import axios from 'axios';
import * as Sentry from '@sentry/vue';
import router from '@/router';
import i18next from '@/config/i18n';
import { API_BASE_URL } from '@/constants';
import { useUserStore } from '@/stores/user';
import { useAppStore } from '@/stores/app';
import { getStoredToken } from '@/utils/authToken';

function reportAxiosError(error, kind) {
    try {
        const cfg = error.config || {};
        const method = (cfg.method || 'GET').toUpperCase();
        const url = cfg.url || '(unknown)';
        const status = error.response?.status ?? 0;
        const detail = error.response?.data?.detail;
        const detailStr = typeof detail === 'string' ? detail : (detail?.message || '');

        Sentry.withScope((scope) => {
            scope.setTag('axios.kind', kind);
            scope.setTag('axios.method', method);
            scope.setTag('axios.status', String(status));
            scope.setTag('axios.url', url);
            scope.setContext('axios', {
                method,
                url,
                status,
                detail: detailStr.slice(0, 500),
                message: error.message,
            });
            scope.setFingerprint(['axios', kind, method, url, String(status)]);
            Sentry.captureException(error);
        });
    } catch (_) {
        // Never let Sentry reporting break the app
    }
}

// Sem `timeout` global, DE PROPÓSITO. Há chamadas legitimamente longas nesta
// API — o render server-side do diagnóstico composto já pede 300s explícitos
// (composables/compositeDiagnosticRenderer.backend.js), a geração de relatório e
// a detecção de áreas (~230s) estão na mesma ordem de grandeza. Um teto global
// baixo abortaria trabalho que o servidor está concluindo; um teto global alto o
// bastante para todos não protegeria nenhum deles de verdade. O prazo é
// característica da CHAMADA, não do cliente: quem tem uma janela conhecida passa
// `timeout` na config (ver useAutoDetectAOI.js).
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    },
    // Custom serializer to handle arrays as repeated keys (e.g. actions=A&actions=B)
    // This is required for FastAPI's default Query(List[str]) handling.
    paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        for (const key in params) {
            const value = params[key];
            if (Array.isArray(value)) {
                value.forEach(val => searchParams.append(key, val));
            } else if (value !== null && value !== undefined) {
                searchParams.append(key, value);
            }
        }
        return searchParams.toString();
    }
});

// Request interceptor to include token in headers
apiClient.interceptors.request.use((config) => {
    const token = getStoredToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Inject Impersonation Header if active.
    // `skipImpersonation: true` na config opta-out: usado em endpoints admin
    // que precisam rodar com o token do superadmin (sem enforce_read_only),
    // ex.: DELETE /admin/experiments/:id chamado de dentro de uma sessão
    // impersonada pra apagar criativo travado de cliente.
    const userStore = useUserStore();
    if (userStore.isImpersonating && userStore.impersonatingUser?.id && !config.skipImpersonation) {
        config.headers['X-Impersonate-User'] = userStore.impersonatingUser.id;
    }

    return config;
}, (error) => {
    return Promise.reject(error);
});

// Flag to prevent multiple redirects for authentication
let isRedirectingForAuth = false;

// SYN-2755 — o 401 veio de um token velho: a request saiu antes de outra aba
// gravar um token novo no localStorage (compartilhado). Deslogar aqui apagaria
// a sessão boa das outras abas; o certo é repetir uma vez com o token atual.
// The 401 came from a token that is no longer the stored one: retry once.
function isStaleTokenFailure(config) {
    const storedToken = getStoredToken();
    if (!storedToken || config._retriedWithStoredToken || config.url === '/token') return false;
    return config.headers?.Authorization !== `Bearer ${storedToken}`;
}

// Response interceptor
apiClient.interceptors.response.use(
    (response) => {
        isRedirectingForAuth = false;
        return response;
    },
    (error) => {
        const appStore = useAppStore();

        if (error.response) {
            const status = error.response.status;
            const data = error.response.data;
            const detail = data?.detail;
            const detailStr = typeof detail === 'string' ? detail : '';

            if (status === 401 && isStaleTokenFailure(error.config)) {
                return apiClient.request({ ...error.config, _retriedWithStoredToken: true });
            }

            // 1. Handle 401 Unauthorized (Token Expired/Invalid)
            if (status === 401 && !isRedirectingForAuth && error.config?.url !== '/token' && !error.config?._skipAuthRedirect) {
                isRedirectingForAuth = true;
                console.log("Axios Interceptor: Caught 401 Unauthorized error.");
                const userStore = useUserStore();
                userStore.logout({ message: i18next.t('errors.unauthorized') });
                setTimeout(() => { isRedirectingForAuth = false; }, 500);
                return Promise.reject(error);
            }

            // 2. Handle Critical 403/400 Errors (Account Status Issues)
            // These errors imply the user session is no longer valid due to account state changes.
            // SYN-2738: the backend now sends `{ code, message }`; the legacy strings stay
            // matched for older deploys. The logout message is the UI-language sentence,
            // never the raw English backend text. `/token` is skipped (like the 401): the
            // login screen shows the reason itself.
            const CRITICAL_CODE_KEYS = {
                client_inactive: 'views.login.errors.inactiveClient', // 403
                user_inactive: 'views.login.errors.inactiveUser',     // 400
            };
            const criticalErrors = {
                "Client account is inactive": 'views.login.errors.inactiveClient', // 403
                "Impersonation not allowed": 'errors.impersonationNotAllowed',     // 403
                "Inactive user": 'views.login.errors.inactiveUser',                // 400
            };
            const criticalKey = (Object.hasOwn(CRITICAL_CODE_KEYS, detail?.code) && CRITICAL_CODE_KEYS[detail.code])
                || criticalErrors[Object.keys(criticalErrors).find(msg => detailStr.includes(msg))];

            if ((status === 403 || status === 400) && criticalKey && error.config?.url !== '/token') {
                if (!isRedirectingForAuth) {
                    isRedirectingForAuth = true;
                    console.log(`Axios Interceptor: Caught critical ${status} (${detail?.code || detailStr}). Logging out.`);
                    const userStore = useUserStore();
                    userStore.logout({ message: i18next.t(criticalKey) });
                    setTimeout(() => { isRedirectingForAuth = false; }, 500);
                }
                return Promise.reject(error);
            }

            // 3. Handle 503 Maintenance Mode
            if (status === 503 && data && data.maintenance === true) {
                console.log("Axios Interceptor: Backend in maintenance mode (503).", data.message);
                appStore.setMaintenanceMode(true, data.message);
                return Promise.resolve({ __isMaintenanceError: true, originalError: error, data: {} });
            }

            // 4. Log Server Errors
            if ([500, 502, 504].includes(status)) {
                console.log(`Axios Interceptor: Service unavailable (${status}). Propagating error.`);
                reportAxiosError(error, 'server_error');
                return Promise.reject(error);
            }

            // 5. Report unexpected 4xx. Skip known user-flow statuses:
            // 401 (auth — already logs out above), 402 (plan limit — handled by callers),
            // 403 (permission denied — user-policy, not a code bug; the critical 403s
            // — inactive account / impersonation blocked — are caught by item 2 above),
            // 429 (limite atingido — é resposta de política, não defeito: o teto de
            // exports simultâneos da SYN-1643 devolve 429 e o caller já avisa o
            // usuário; reportar geraria um evento por clique impaciente).
            // `_skipErrorReport` na config opta-out por request: usado quando o
            // erro é esperado e já tratado pelo caller — 404 de endpoint que ainda não
            // existe em todos os ambientes, ou 4xx que é fluxo normal de produto
            // (SYN-1519: convite de primeiro acesso expirado/usado devolve 400 por
            // contrato). Reportar esses casos só geraria ruído no Sentry.
            //
            // Aceita `true` (silencia TODO 4xx daquela chamada) ou uma LISTA de
            // status (silencia só esses). A lista existe porque nem toda rota tem
            // um único tipo de 4xx: em `POST /aois/auto-detect/:id` o 409 é fluxo
            // normal de produto — é o mecanismo com que o backend avisa "as áreas
            // já existem", e cada retry bem-sucedido-por-outro-caminho da etapa 3
            // gerava um evento —, enquanto o 422 e o 404 da MESMA rota continuam
            // sendo defeito que vale acordar alguém. `true` silenciaria os três.
            const skipReport = error.config?._skipErrorReport;
            const skipThisStatus = skipReport === true
                || (Array.isArray(skipReport) && skipReport.includes(status));
            if (status >= 400 && status < 500 && status !== 401 && status !== 402 && status !== 403
                && status !== 429 && !skipThisStatus) {
                reportAxiosError(error, 'client_error');
            }
        } else if (error.request) {
             // Network error
        }

        /* istanbul ignore next -- 503 maintenance always returns early above; inner maintenance guard is unreachable here */
        if (error.request && !(error.response && error.response.status === 503 && error.response.data && error.response.data.maintenance === true)) {
            console.log("Axios Interceptor: Network error. Propagating error.", error.message);
            reportAxiosError(error, 'network_error');
            return Promise.reject(error);
        }

        return Promise.reject(error);
    }
);

export default apiClient;
